// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: an HTML page in an opaque-origin frame. The document policy blocks remote resources, and
// the sandbox prevents access to Konvo, popups and navigation of the parent. Only the viewer, opened
// deliberately, runs the page's scripts: a sandboxed srcdoc frame can share Konvo's main thread
// (WebKit), so a busy loop in a card would freeze the app for everyone who scrolls past it.
// Thumbnails are script-free, lay out at desktop width and scale down to fit (a page made for the
// chat lays out at the chat's own width instead). With `assets`, the
// page's own pictures and stylesheets come along (HtmlAssets.ts); with `webPictures` (given, or
// inherited from WebPicturesContext), its pictures, sound and video from the web come through
// Konvo's media proxy (HtmlWebPictures.ts). In the viewer, scripts and stylesheets from a few CDNs
// go into the page, and their pictures and map tiles come through the proxy (HtmlWebLibraries.ts).

import styles from '@app/features/document_preview/DocumentPreview.module.css';
import {htmlPreviewDocument} from '@app/features/document_preview/DocumentPreviewUtils';
import {
	type HtmlAssetLoader,
	type HtmlAssetMode,
	type PreparedHtml,
	prepareHtmlAssets,
} from '@app/features/document_preview/HtmlAssets';
import {PageSnapshotsContext, pageHasScript, pageSnapshot} from '@app/features/document_preview/HtmlSnapshot';
import {
	type HtmlLibraryLoader,
	pageUsesWebLibraries,
	prepareWebLibraries,
} from '@app/features/document_preview/HtmlWebLibraries';
import {
	prepareWebPictures,
	useWebPictures,
	useWebPicturesSource,
	type WebPictures,
	webPictureSource,
} from '@app/features/document_preview/HtmlWebPictures';
import {Spinner} from '@app/features/ui/components/Spinner';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import type React from 'react';
import {useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

/** The width a thumbnail lays the page out at before scaling it down. */
const THUMBNAIL_PAGE_WIDTH = 1200;
/** The width a page made for the chat builds its still at. */
const FIT_PAGE_WIDTH = 720;
/** Files fetched at once for a page frame. */
const ASSET_CONCURRENCY = 4;

const plainPage = (html: string): PreparedHtml => ({html, lazy: new Map(), pictures: 0});

/**
 * The page with its local files and web pictures in place, and whether more is still coming. A
 * thumbnail shows the styled page first and swaps in its first pictures once they arrive, or the
 * still of the page as its script builds it (`snapshot`: the width to build it at; HtmlSnapshot);
 * the page view loads its pictures in the frame. Null while nothing is ready yet; the page as it is
 * without `assets`, `webPictures`, `snapshot` or `libraries`. With `libraries`, the page view gets
 * the scripts and stylesheets it loads from the CDNs (HtmlWebLibraries); null: still to come.
 */
function usePreparedHtml(
	html: string,
	assets: HtmlAssetLoader | undefined,
	webPictures: WebPictures | undefined,
	thumbnail: boolean,
	snapshot: number | null,
	libraries: HtmlLibraryLoader | null | undefined,
): {prepared: PreparedHtml | null; pending: boolean} {
	const plain = useMemo(
		() => (assets || webPictures || snapshot || libraries !== undefined ? null : plainPage(html)),
		[assets, webPictures, snapshot, libraries, html],
	);
	const [state, setState] = useState<{prepared: PreparedHtml | null; pending: boolean}>({
		prepared: null,
		pending: true,
	});
	useEffect(() => {
		setState({prepared: null, pending: true});
		if (libraries === null || (!assets && !webPictures && !snapshot && !libraries)) return;
		const controller = new AbortController();
		const prepare = async (source: string, mode: HtmlAssetMode) => {
			let page = assets
				? await prepareHtmlAssets(source, assets, mode, controller.signal).catch(() => plainPage(source))
				: plainPage(source);
			if (libraries && mode === 'page') {
				const withLibraries = await prepareWebLibraries(page.html, libraries).catch(() => page.html);
				page = {...page, html: withLibraries};
			}
			if (!webPictures) return page;
			const withWeb = await prepareWebPictures(page.html, webPictures, mode === 'page', controller.signal).catch(
				() => page.html,
			);
			return {...page, html: withWeb};
		};
		void (async () => {
			const first = await prepare(html, thumbnail ? 'outline' : 'page');
			if (controller.signal.aborted) return;
			const more = thumbnail && (first.pictures > 0 || !!snapshot);
			setState({prepared: first, pending: more});
			if (!more) return;
			// The still, when the page's script built one in time; else the page as it is.
			const built = snapshot ? await pageSnapshot(html, snapshot) : null;
			if (controller.signal.aborted) return;
			const full = built || first.pictures > 0 ? await prepare(built ?? html, 'thumbnail') : first;
			if (!controller.signal.aborted) setState({prepared: full, pending: false});
		})();
		return () => controller.abort();
	}, [assets, webPictures, html, thumbnail, snapshot, libraries]);
	return plain ? {prepared: plain, pending: false} : state;
}

type PageWebFiles = typeof import('@app/features/document_preview/PageWebFiles');

/**
 * Where the page view gets the files a page loads from the CDNs, loaded when a page first needs it:
 * null while it comes, undefined when the page needs none (or it couldn't be loaded).
 */
function usePageWebFiles(wanted: boolean): PageWebFiles | null | undefined {
	const [files, setFiles] = useState<PageWebFiles | false | null>(null);
	useEffect(() => {
		if (!wanted || files !== null) return;
		let live = true;
		import('@app/features/document_preview/PageWebFiles').then(
			(module) => live && setFiles(module),
			() => live && setFiles(false),
		);
		return () => {
			live = false;
		};
	}, [wanted, files]);
	if (!wanted || files === false) return undefined;
	return files;
}

/**
 * Answers the page frame's requests for the files prepareHtmlAssets marked, a few at a time. The
 * frame's script hands over a MessageChannel port once (from the frame, still on its opaque
 * srcdoc origin); files go back over that port only, never to the frame window itself, so a page
 * that navigates its frame elsewhere gets nothing more.
 */
function useLazyAssets(
	frame: React.RefObject<HTMLIFrameElement | null>,
	lazy: Map<string, string> | undefined,
	assets: HtmlAssetLoader | undefined,
): boolean {
	const [loading, setLoading] = useState(false);
	useEffect(() => {
		setLoading(false);
		if (!assets || !lazy?.size) return;
		const controller = new AbortController();
		const queue: Array<string> = [];
		const asked = new Set<string>();
		let port: MessagePort | null = null;
		let running = 0;
		const pump = () => {
			while (running < ASSET_CONCURRENCY && queue.length) {
				const id = queue.shift()!;
				running++;
				void assets(lazy.get(id)!, controller.signal)
					.then((blob) => {
						if (!controller.signal.aborted) port?.postMessage({konvoAsset: id, blob});
					})
					.catch(() => {})
					.finally(() => {
						running--;
						pump();
					});
			}
			if (!controller.signal.aborted) setLoading(running > 0 || queue.length > 0);
		};
		const onRequest = (event: MessageEvent) => {
			const id = (event.data as {konvoAsset?: unknown} | null)?.konvoAsset;
			if (typeof id !== 'string' || !lazy.has(id) || asked.has(id)) return;
			asked.add(id);
			queue.push(id);
			pump();
		};
		const onMessage = (event: MessageEvent) => {
			if (!frame.current || event.source !== frame.current.contentWindow || event.origin !== 'null') return;
			if ((event.data as {konvoAssets?: unknown} | null)?.konvoAssets !== 1 || !event.ports[0]) return;
			if (port) {
				// One channel per page: a second (another script, or a page the frame went to) gets none.
				event.ports[0].close();
				return;
			}
			port = event.ports[0];
			port.onmessage = onRequest;
		};
		window.addEventListener('message', onMessage);
		return () => {
			controller.abort();
			window.removeEventListener('message', onMessage);
			port?.close();
		};
	}, [assets, frame, lazy]);
	return loading;
}

export function HtmlFrame({
	html,
	title,
	thumbnail = false,
	height,
	fit = false,
	deviceWidth,
	assets,
	webPictures,
	className,
}: {
	html: string;
	title: string;
	/** A still picture of the page (scaled, not interactive), for cards. */
	thumbnail?: boolean;
	/** The thumbnail's height on screen. */
	height?: number;
	/** Lay a thumbnail out at its own width, unscaled: a page made to be read in the chat (T3's html_render). */
	fit?: boolean;
	/** Lay the page out at this width (tablet, phone); else the full width. */
	deviceWidth?: number;
	/** Loads the pictures, stylesheets and fonts the page names beside it; without it they stay out. */
	assets?: HtmlAssetLoader;
	/** Gives the page's pictures from the web through the media proxy; else the inherited source, else they stay out. */
	webPictures?: WebPictures;
	className?: string;
}) {
	const {t} = useLingui();
	// The page view of a page that loads libraries from the CDNs gets them, and their pictures and
	// map tiles through the proxy even where no wider source is set (pages sent in chats).
	const pageFiles = usePageWebFiles(!thumbnail && pageUsesWebLibraries(html));
	const libraries = pageFiles === null ? null : pageFiles?.PAGE_WEB_LIBRARIES;
	const source = useWebPicturesSource(webPictures) ?? pageFiles?.PAGE_WEB_PICTURES;
	// Without a media proxy address the policy can admit, web pictures stay out as before.
	const imageSource = source ? webPictureSource(source.base) : null;
	const web = imageSource ? source : undefined;
	// A card of a page with script, where cards may run it (Agents), shows the page as it builds.
	const stills = useContext(PageSnapshotsContext);
	const snapshot = thumbnail && stills && pageHasScript(html) ? (fit ? FIT_PAGE_WIDTH : THUMBNAIL_PAGE_WIDTH) : null;
	const {prepared, pending} = usePreparedHtml(html, assets, web, thumbnail, snapshot, libraries);
	const srcDoc = useMemo(
		() => (prepared ? htmlPreviewDocument(prepared.html, imageSource ? [imageSource] : []) : undefined),
		[prepared, imageSource],
	);
	const frame = useRef<HTMLIFrameElement | null>(null);
	const loadingPictures = useLazyAssets(frame, thumbnail ? undefined : prepared?.lazy, assets);
	useWebPictures(frame, thumbnail ? undefined : web);
	const box = useRef<HTMLDivElement | null>(null);
	const [width, setWidth] = useState(0);
	useLayoutEffect(() => {
		const element = box.current;
		if (!thumbnail || !element) return;
		setWidth(element.clientWidth);
		if (typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(() => setWidth(element.clientWidth));
		observer.observe(element);
		return () => observer.disconnect();
	}, [thumbnail]);

	if (thumbnail) {
		const pageWidth = fit ? Math.max(width, 1) : THUMBNAIL_PAGE_WIDTH;
		const scale = fit ? 1 : width > 0 ? Math.min(1, width / THUMBNAIL_PAGE_WIDTH) : 0.5;
		const boxHeight = height ?? 260;
		return (
			<div ref={box} className={clsx(styles.frameBox, className)} style={{height: boxHeight}} aria-hidden="true">
				<iframe
					className={styles.frameThumb}
					title={title}
					sandbox=""
					referrerPolicy="no-referrer"
					srcDoc={srcDoc}
					tabIndex={-1}
					loading="lazy"
					style={{
						width: pageWidth,
						height: Math.ceil(boxHeight / scale),
						transform: `scale(${scale})`,
					}}
				/>
				{pending && (
					<span className={styles.frameSpinner}>
						<Spinner size="small" />
					</span>
				)}
			</div>
		);
	}
	return (
		<div className={clsx(styles.frameFill, deviceWidth && styles.frameDevice, className)}>
			<iframe
				ref={frame}
				className={styles.frame}
				title={title}
				sandbox="allow-scripts"
				referrerPolicy="no-referrer"
				srcDoc={srcDoc}
				style={deviceWidth ? {width: deviceWidth} : undefined}
				data-flx="document-preview.html-frame"
			/>
			{(pending || loadingPictures) && (
				<span className={styles.frameLoading} role="status" data-flx="document-preview.html-frame.loading">
					<Spinner size="small" />
					{prepared
						? t({
								message: 'Loading pictures',
								comment: 'Document viewer: an HTML page’s pictures are still coming from the computer it is on.',
							})
						: t({message: 'Loading page', comment: 'Document viewer: an HTML page is getting ready to show.'})}
				</span>
			)}
		</div>
	);
}
